import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { Copy, Check, Eye, EyeOff } from 'lucide-react';

interface QRCodeDisplayProps {
  qrCodeUrl: string;
  secret: string;
  className?: string;
}

export function QRCodeDisplay({ qrCodeUrl, secret, className }: QRCodeDisplayProps): JSX.Element {
  const [copied, setCopied] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  const copySecret = useCallback(async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => { setCopied(false); }, 2000);
    } catch {
      // Clipboard API not available - show manual copy hint
      setCopied(false);
    }
  }, [secret]);

  return (
    <div className={cn('space-y-6', className)}>
      {/* QR Code */}
      <div className="flex justify-center" data-testid="2fa-setup-qr-code">
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <img
            src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrCodeUrl)}`}
            alt="Scan this QR code with your authenticator app"
            className="w-[200px] h-[200px]"
          />
        </div>
      </div>

      {/* Manual Entry */}
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground text-center">
          Can&apos;t scan the QR code? Enter this code manually:
        </p>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Input
              type={showSecret ? 'text' : 'password'}
              value={secret}
              readOnly
              className="pr-20 font-mono text-sm"
              data-testid="2fa-setup-secret"
            />
            <div className="absolute right-1 top-1/2 -translate-y-1/2 flex gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => { setShowSecret(!showSecret); }}
              >
                {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => { void copySecret(); }}
              >
                {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="text-sm text-muted-foreground space-y-2">
        <p className="font-medium">Recommended authenticator apps:</p>
        <ul className="list-disc list-inside space-y-1 pl-2">
          <li>Google Authenticator</li>
          <li>Microsoft Authenticator</li>
          <li>Authy</li>
          <li>1Password</li>
        </ul>
      </div>
    </div>
  );
}
